import { SEARCH_MIN_LENGTH, useKeyboardNavigation, useSearchItems } from "../command-bar/common";
import { GiftCardSearchEntry, SearchResults, giftCardSearchUrl } from "../command-bar/search-result-render";
import { LoadingSpinner } from "../loading";
import { useProgressBar, useProgressNavigation } from "../progress-bar";
import { Kbd } from "../ui/kbd";
import { ScrollShadow } from "../ui/scroll-shadow";
import * as DialogPrimitives from "@radix-ui/react-dialog";
import { debounce } from "lodash";
import { motion } from "motion/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaSearch } from "react-icons/fa";
import { usePathname } from "next/navigation";
import { CatalogSearchItem } from "@/components/command-bar/item-display";
import { getTenantSwitchPath } from "@/components/navigation/switch-tenant-menu";
import { CommandAction, filterCommandActions, useCommandActions } from "@/config/command-actions";
import { queryClient } from "@/config/react-query";
import { SiteMapItem, getFilteredCommandBarRoutes } from "@/config/sitemap";
import { useTenant } from "@/contexts/tenant-provider";
import { ThemeMode, useTheme } from "@/contexts/theme-provider";
import { useUser } from "@/contexts/user-provider";
import { useLocations } from "@/hooks/use-locations";
import { useMayHaveMultipleTenants } from "@/hooks/use-may-have-multiple-tenants";
import { useModule } from "@/hooks/use-module";
import { useTenantSwitch } from "@/hooks/use-tenant-switch";
import { parsePatientNameFromSearch } from "@/lib/patient-search-prefill";
import { PERMISSIONS, hasAnyPermission, hasAnyRole, hasPagePermissions } from "@/lib/permissions";
import { SearchResultType, trackResultSelected, trackSearchQuery } from "@/lib/search-analytics";
import { updateRecentPatients, usePageViewTracking } from "@/lib/use-page-view-tracking";
import { useToast } from "@/lib/use-toast";
import { ItemType } from "@/types/enums/item-type";
import { LocationDetail } from "@/types/location";
import { PatientSummary } from "@/types/patient";
import { UserTiny } from "@/types/user";
import { searchGiftCardsByCode } from "@/api/provider/billing/gift-card";
import { searchItems } from "@/api/provider/inventory/item";
import { patientSearchQuery } from "@/api/provider/user/patient/patient-search-query";
import { askDecodaSidebarDefinition, assistantSidebarDefinition } from "@/features/sidebar/assistant-sidebar";
import { useAvailableTenants } from "@/query/tenant";
import { useAskDecodaStore } from "@/stores/ask-decoda-store";
import { CommandPaletteModal, useCommandPaletteModalsStore } from "@/stores/command-palette-modals-store";
import { useNavigationSearchOpenStore } from "@/stores/navigation-search-store";
import { useRecentItemsStore } from "@/stores/recent-patients-store";
import { useRightSidebarStore } from "@/stores/right-sidebar-store";
export const NavigationSearch = () => {
  usePageViewTracking();
  const pathname = usePathname();
  const progress = useProgressBar();
  const {
    tenant
  } = useTenant();
  const {
    currentUser,
    currentLocationId,
    updatePrimaryLocation
  } = useUser();
  const {
    locations
  } = useLocations();
  const taxLocationId = useMemo(() => currentLocationId && locations.some(location => location.id === currentLocationId) ? currentLocationId : undefined, [currentLocationId, locations]);
  const {
    resolvedTheme,
    setTheme,
    customTheme
  } = useTheme();
  const {
    toast
  } = useToast();
  const {
    getRecentPatients,
    getRecentPages
  } = useRecentItemsStore();
  const hasAskDecoda = useModule("ASK_DECODA");
  const hasGiftCards = useModule("GIFT_CARDS");
  const onClinicDesk = tenant?.kind !== "MASTER";
  // Match the destination's access checks so we never surface a selectable
  // result that routes to a page the user can't open: the /settings/gift-cards
  // route is gated on INVENTORY_READ, while its issued-cards data (and this
  // search endpoint) require BILLING_READ. Require both.
  const canSearchGiftCards = onClinicDesk && hasGiftCards && hasAnyPermission(currentUser, [PERMISSIONS.INVENTORY_READ]) && hasAnyPermission(currentUser, [PERMISSIONS.BILLING_READ]);
  const {
    isOpen,
    setIsOpen,
    closeSearch
  } = useNavigationSearchOpenStore();
  // Track whether current selection is via keyboard (for analytics)
  const selectionMethodRef = useRef<"keyboard" | "click">("click");
  const [search, setSearch] = useState("");
  const deskSearch = onClinicDesk ? search : "";
  const [isPatientLoading, setIsPatientLoading] = useState(false);
  const [isCatalogLoading, setIsCatalogLoading] = useState(false);
  const isLoading = isPatientLoading || isCatalogLoading;
  const [patients, setPatients] = useState<PatientSummary[]>([]);
  const [catalogItems, setCatalogItems] = useState<CatalogSearchItem[]>([]);
  // Gift card loading is deliberately excluded from `isLoading` above so the
  // input spinner reflects only the primary search — gift cards are a last,
  // best-effort section and must never make the main search feel slower.
  const [giftCards, setGiftCards] = useState<GiftCardSearchEntry[]>([]);
  const [isGiftCardLoading, setIsGiftCardLoading] = useState(false);
  const [filteredPages, setFilteredPages] = useState<SiteMapItem[]>([]);
  const [filteredThemeModes, setFilteredThemeModes] = useState<ThemeMode[]>([]);
  const [selectedRow, setSelectedRow] = useState<number | undefined>(undefined);
  const [recentItems, setRecentItems] = useState<{
    patients: UserTiny[];
    pages: string[];
  }>({
    patients: [],
    pages: []
  });
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const {
    navigateWithProgress,
    runWithNavigationGuard
  } = useProgressNavigation();

  // Get command actions with mutations
  const commandActions = useCommandActions();
  const mayHaveMultipleTenants = useMayHaveMultipleTenants();
  const {
    data: availableTenants = []
  } = useAvailableTenants({
    enabled: isOpen && mayHaveMultipleTenants
  });
  const tenants = useMemo(() => availableTenants && availableTenants.length > 1 ? availableTenants : [], [availableTenants]);

  // Filter tenants based on search
  const filteredTenants = useMemo(() => {
    if (search.length < SEARCH_MIN_LENGTH) return [];
    const searchLower = search.toLowerCase();
    return tenants.filter(t => t.name.toLowerCase().includes(searchLower) || t.subDomain.toLowerCase().includes(searchLower));
  }, [search, tenants]);

  // Filter locations based on search; prepend "All Locations" when there are
  // multiple locations so the user can target all of them at once.
  const filteredLocations = useMemo<LocationDetail[]>(() => {
    if (deskSearch.length < SEARCH_MIN_LENGTH) return [];
    const candidates: LocationDetail[] = locations.length > 1 ? [{
      id: "all-locations",
      name: "All Locations",
      isVirtual: false,
      isArchived: false,
      timezone: locations[0].timezone
    }, ...locations] : locations;
    const searchLower_0 = deskSearch.toLowerCase();
    return candidates.filter(loc => loc.name.toLowerCase().includes(searchLower_0) || loc.city?.toLowerCase().includes(searchLower_0) || loc.state?.toLowerCase().includes(searchLower_0));
  }, [deskSearch, locations]);

  // Filter theme modes based on search (only show opposite theme)
  useEffect(() => {
    if (search.length < SEARCH_MIN_LENGTH) {
      setFilteredThemeModes([]);
      return;
    }
    const searchLower_1 = search.toLowerCase();
    // Only show the opposite theme option
    const oppositeTheme: ThemeMode = resolvedTheme === "light" ? "dark" : "light";

    // Check if search matches the opposite theme
    const matches = oppositeTheme.toLowerCase().includes(searchLower_1) || oppositeTheme === "light" && ("light".includes(searchLower_1) || "switch to light theme".includes(searchLower_1) || "light theme".includes(searchLower_1)) || oppositeTheme === "dark" && ("dark".includes(searchLower_1) || "switch to dark theme".includes(searchLower_1) || "dark theme".includes(searchLower_1)) || "theme".includes(searchLower_1) || "switch".includes(searchLower_1) || "appearance".includes(searchLower_1);
    setFilteredThemeModes(matches ? [oppositeTheme] : []);
  }, [search, resolvedTheme]);
  const filteredActions = useMemo(() => {
    const actions = filterCommandActions(commandActions.actions, search, currentUser, SEARCH_MIN_LENGTH, tenant);
    const settings = filterCommandActions(commandActions.settings, search, currentUser, SEARCH_MIN_LENGTH, tenant);
    return {
      actions,
      settings
    };
  }, [search, currentUser, commandActions, tenant]);

  // Generate glow shadow based on theme's primary color
  const glowShadow = useMemo(() => {
    // Get RGB values from theme - customTheme.colors.primary is in "R G B" format
    const primaryRgb = (customTheme?.colors?.primary ?? (typeof document !== "undefined" ? getComputedStyle(document.documentElement).getPropertyValue("--primary").trim() : "")) || (resolvedTheme === "dark" ? "56 178 255" : "1 120 212");
    const [r, g, b] = primaryRgb.split(" ").map(Number);

    // Adjust intensity based on theme mode
    const pulseOpacity = resolvedTheme === "dark" ? 0.19 : 0.25;

    // Asymmetric glow - strong top-left, weak top-right, medium center, subtle bottom-right
    return `-38px -30px 70px rgba(${r}, ${g}, ${b}, ${pulseOpacity}), 25px -15px 45px rgba(${r}, ${g}, ${b}, ${pulseOpacity * 0.3}), -5px -50px 85px rgba(${r}, ${g}, ${b}, ${pulseOpacity * 0.55}), 18px 25px 45px rgba(${r}, ${g}, ${b}, ${pulseOpacity * 0.25})`;
  }, [customTheme, resolvedTheme]);
  const {
    suggestedPages,
    recentPages,
    currentItems
  } = useSearchItems(search, filteredPages, patients, recentItems);
  const canCreatePatient = onClinicDesk && hasAnyPermission(currentUser, [PERMISSIONS.PATIENTS_CREATE, PERMISSIONS.PATIENTS_PROFILES_WRITE]);
  const parsedPatientName = useMemo(() => parsePatientNameFromSearch(search), [search]);
  const showCreatePatientCta = canCreatePatient && search.length >= SEARCH_MIN_LENGTH && !isPatientLoading && patients.length === 0 && parsedPatientName !== null;
  const searchResultCount = patients.length + filteredPages.length + catalogItems.length + giftCards.length + filteredTenants.length + filteredLocations.length + filteredThemeModes.length + filteredActions.actions.length + filteredActions.settings.length + (showCreatePatientCta ? 1 : 0);

  // Only show "Ask Decoda" after all searches complete with no results.
  // Includes the gift card search (which is excluded from `isLoading`) so the
  // fallback doesn't flash before a late-arriving gift card match.
  const showAskDecoda = hasAskDecoda && search.length >= SEARCH_MIN_LENGTH && !isLoading && !isGiftCardLoading && searchResultCount === 0;
  const totalRows = search.length < SEARCH_MIN_LENGTH ? suggestedPages.length + recentPages.length + recentItems.patients.length : searchResultCount + (showAskDecoda ? 1 : 0);
  useKeyboardNavigation(isOpen, setIsOpen, totalRows, selectedRow, setSelectedRow, index => {
    // Mark this as a keyboard selection for analytics
    selectionMethodRef.current = "keyboard";

    // Check if "Ask Decoda" row is selected (last item when searching)
    if (showAskDecoda && index === totalRows - 1) {
      handleAskDecoda(search);
      return;
    }
    const items = search.length < SEARCH_MIN_LENGTH ? [...suggestedPages, ...recentPages, ...recentItems.patients] : [...filteredActions.actions, ...filteredPages, ...filteredActions.settings, ...filteredLocations, ...filteredThemeModes, ...filteredTenants, ...patients, ...(showCreatePatientCta ? [{
      _kind: "create-patient" as const
    }] : []), ...catalogItems, ...giftCards];
    const item = items[index];
    if (!item) return;
    if (typeof item === "object" && "_kind" in item && item._kind === "create-patient") {
      handleCreatePatient(index);
      return;
    }

    // Check for theme mode first (it's a string)
    if (typeof item === "string" && (item === "light" || item === "dark")) {
      handleThemeModeSelect(item as ThemeMode, index);
      return;
    }

    // Type guard: item must be an object at this point
    if (typeof item !== "object") return;
    if ("subDomain" in item) {
      handleTenantSelect(item.subDomain, index);
    } else if ("isVirtual" in item && "timezone" in item) {
      // This is a location — isVirtual + timezone are required fields on
      // every LocationDetail and absent from the other item shapes.
      handleLocationSelect(item.id, index);
    } else if ("category" in item && "onSelect" in item) {
      // This is a command action (category + onSelect is unique to CommandAction)
      handleActionSelect(item as CommandAction, index);
    } else if ("_kind" in item && item._kind === "catalog") {
      // This is a catalog item (product/service/membership/package)
      const catalogItem = item as CatalogSearchItem;
      const url = `/settings/${catalogItem.type.toLowerCase()}s?id=${encodeURIComponent(catalogItem.id)}`;
      const resultType = catalogItem.type.toLowerCase() as SearchResultType;
      handleSelect(url, resultType, index);
    } else if ("_kind" in item && item._kind === "gift-card") {
      // This is a gift card matched by code
      handleSelect(giftCardSearchUrl(item.id), "gift_card", index);
    } else {
      const url_0 = "id" in item ? `/patient/${item.id}` : (item as SiteMapItem).route;
      // Determine result type based on context
      const resultType_0: SearchResultType = search.length < SEARCH_MIN_LENGTH ? "id" in item ? "recent_patient" : suggestedPages.includes(item as SiteMapItem) ? "suggested_page" : "recent_page" : "id" in item ? "patient" : "page";
      if (url_0) handleSelect(url_0, resultType_0, index);
    }
  });
  const debouncedFetchPatients = useCallback(debounce(async (searchTerm: string, controller: AbortController) => {
    if (searchTerm.length >= SEARCH_MIN_LENGTH) {
      setIsPatientLoading(true);
      const results = await queryClient.fetchQuery(patientSearchQuery.list(searchTerm, controller));
      setPatients(results ?? []);
      setIsPatientLoading(false);
    } else {
      setPatients([]);
    }
  }, 300), []);
  useEffect(() => {
    if (deskSearch.length >= SEARCH_MIN_LENGTH) {
      setIsPatientLoading(true);
    }
    const controller_0 = new AbortController();
    debouncedFetchPatients(deskSearch, controller_0);
    return () => {
      controller_0.abort();
      debouncedFetchPatients.cancel();
    };
  }, [deskSearch, debouncedFetchPatients]);

  // Fetch catalog items (products, services, memberships, packages) when search changes
  const debouncedFetchCatalogItems = useCallback(debounce(async (searchTerm_0: string, controller_1: AbortController) => {
    if (searchTerm_0.length >= SEARCH_MIN_LENGTH) {
      setIsCatalogLoading(true);
      try {
        const result = await searchItems(searchTerm_0, [ItemType.PRODUCT, ItemType.SERVICE, ItemType.MEMBERSHIP, ItemType.PACKAGE, ItemType.MEDICATION],
        // Omit the limit so the backend returns every word-prefix match, not just the top few.
        undefined, taxLocationId, controller_1.signal);
        if (!controller_1.signal.aborted) {
          setCatalogItems((result ?? []).filter(item_0 => item_0.price !== 0).map(item_1 => ({
            _kind: "catalog" as const,
            id: item_1.id,
            name: item_1.name,
            price: item_1.price,
            type: item_1.type,
            taxable: item_1.taxable,
            discountTemplates: item_1.discountTemplates
          })));
        }
      } catch (error) {
        if (!controller_1.signal.aborted) {
          console.error("Failed to fetch catalog items:", error);
          setCatalogItems([]);
        }
      } finally {
        if (!controller_1.signal.aborted) {
          setIsCatalogLoading(false);
        }
      }
    } else {
      setCatalogItems([]);
      setIsCatalogLoading(false);
    }
  }, 300), [taxLocationId]);
  useEffect(() => {
    const controller_2 = new AbortController();
    debouncedFetchCatalogItems(deskSearch, controller_2);
    return () => {
      controller_2.abort();
      debouncedFetchCatalogItems.cancel();
    };
  }, [deskSearch, debouncedFetchCatalogItems]);

  // Fetch gift cards by code. Runs as an independent, non-blocking request (it
  // never feeds `isLoading`) and renders last, so it can't slow the primary
  // search. Gated by the gift cards module + billing read permission.
  const debouncedFetchGiftCards = useCallback(debounce(async (searchTerm_1: string, controller_3: AbortController) => {
    if (searchTerm_1.length < SEARCH_MIN_LENGTH) {
      setGiftCards([]);
      setIsGiftCardLoading(false);
      return;
    }
    setIsGiftCardLoading(true);
    try {
      const result_0 = await searchGiftCardsByCode(searchTerm_1, controller_3.signal);
      if (!controller_3.signal.aborted) {
        setGiftCards((result_0 ?? []).map(gc => ({
          ...gc,
          _kind: "gift-card" as const
        })));
      }
    } catch (error_0) {
      if (!controller_3.signal.aborted) {
        console.error("Failed to fetch gift cards:", error_0);
        setGiftCards([]);
      }
    } finally {
      if (!controller_3.signal.aborted) {
        setIsGiftCardLoading(false);
      }
    }
  }, 300), []);
  useEffect(() => {
    if (!canSearchGiftCards) {
      setGiftCards([]);
      setIsGiftCardLoading(false);
      return;
    }
    const controller_4 = new AbortController();
    debouncedFetchGiftCards(search, controller_4);
    return () => {
      controller_4.abort();
      debouncedFetchGiftCards.cancel();
    };
  }, [search, canSearchGiftCards, debouncedFetchGiftCards]);

  // Track search queries (debounced to avoid tracking every keystroke)
  const debouncedTrackSearch = useMemo(() => debounce((queryLength: number) => {
    trackSearchQuery(queryLength);
  }, 500), []);
  useEffect(() => {
    if (search.length >= SEARCH_MIN_LENGTH) {
      debouncedTrackSearch(search.length);
    }
    return () => {
      debouncedTrackSearch.cancel();
    };
  }, [search, debouncedTrackSearch]);

  // Filter pages when search changes
  useEffect(() => {
    setSelectedRow(0);
    if (search.length >= SEARCH_MIN_LENGTH) {
      const searchLower_2 = search.toLowerCase();
      const allPages = getFilteredCommandBarRoutes(tenant).filter(page => {
        const hasRequiredRoles = !page.roles || hasAnyRole(currentUser, page.roles);
        const hasRequiredPermissions = hasPagePermissions(currentUser, page);
        return hasRequiredRoles && hasRequiredPermissions;
      }).filter(page_0 => {
        const nameMatch = page_0.name.toLowerCase().includes(searchLower_2);
        const descriptionMatch = page_0.description?.toLowerCase().includes(searchLower_2);
        const keywordMatch = page_0.keywords?.some(k => k.toLowerCase().includes(searchLower_2));
        return nameMatch || descriptionMatch || keywordMatch;
      }).map(page_1 => {
        const nameMatch_0 = page_1.name.toLowerCase().includes(searchLower_2);
        const descriptionMatch_0 = page_1.description?.toLowerCase().includes(searchLower_2);
        const keywordMatch_0 = page_1.keywords?.some(k_0 => k_0.toLowerCase().includes(searchLower_2));

        // Sort pages by match priority: exact name, name prefix, other name
        // matches, then keywords, then descriptions
        const nameLower = page_1.name.toLowerCase();
        let priority = 0;
        if (nameLower === searchLower_2) priority = 1;else if (nameLower.startsWith(searchLower_2)) priority = 2;else if (nameMatch_0) priority = 3;else if (keywordMatch_0) priority = 4;else if (descriptionMatch_0) priority = 5;
        return {
          page: page_1,
          priority
        };
      }).sort((a, b_0) => a.priority - b_0.priority).map(item_2 => item_2.page);
      setFilteredPages(allPages);
    } else {
      setFilteredPages([]);
    }
  }, [search, currentUser?.roles, tenant, currentUser?.email]);

  // Fetch recent items when dialog opens
  useEffect(() => {
    if (isOpen && tenant) {
      const recentPages_0 = getRecentPages();
      const accessibleRecentPages = recentPages_0.filter(page_2 => {
        const siteMapPage = getFilteredCommandBarRoutes(tenant).find(route => route.route === page_2);
        if (!siteMapPage) return false;
        const hasRequiredRoles_0 = !siteMapPage.roles || hasAnyRole(currentUser, siteMapPage.roles);
        const hasRequiredPermissions_0 = hasPagePermissions(currentUser, siteMapPage);
        return hasRequiredRoles_0 && hasRequiredPermissions_0;
      });
      setRecentItems({
        pages: accessibleRecentPages,
        patients: onClinicDesk ? getRecentPatients(tenant.subDomain) : []
      });
    } else if (!isOpen) {
      setSearch("");
    }
  }, [isOpen, tenant, currentUser?.roles, currentUser?.email]);

  // Helper to track selection and reset selection method
  const trackSelection = (resultType_1: SearchResultType, resultIndex: number, actionId?: string) => {
    trackResultSelected(resultType_1, selectionMethodRef.current, search.length, resultIndex, actionId);
    selectionMethodRef.current = "click";
  };

  // Handle selection
  const handleSelect = (url_1: string, resultType_2: SearchResultType = "page", resultIndex_0: number = 0) => {
    trackSelection(resultType_2, resultIndex_0);

    // If it's a patient URL, find the patient in our current list and update recent patients
    const patientMatch = url_1.match(/^\/patient\/([^/]+)/);
    if (patientMatch) {
      const patient = [...patients, ...recentItems.patients].find(p => p.id === patientMatch[1]);
      if (patient) {
        updateRecentPatients(patient as UserTiny);
      }
    }
    setSearch("");
    navigateWithProgress(url_1);
    setSelectedRow(undefined);
    closeSearch("selection");
  };
  const switchTenant = useTenantSwitch();
  const handleTenantSelect = (subDomain: string, resultIndex_1: number = 0) => {
    if (!currentUser?.firebaseUid) return;
    trackSelection("tenant", resultIndex_1);
    const target = tenants.find(t_0 => t_0.subDomain === subDomain);
    if (!target) return;
    setSearch("");
    setSelectedRow(undefined);
    closeSearch("selection");
    runWithNavigationGuard(`/${subDomain}`, () => {
      void switchTenant(subDomain, () => getTenantSwitchPath(pathname, target));
    }, false);
  };
  const handleLocationSelect = async (locationId: string | null, resultIndex_2: number = 0) => {
    if (!currentUser?.id) return;
    trackSelection("location", resultIndex_2);
    if (locationId === "all-locations") {
      locationId = null;
    }
    progress.start();
    const success = await updatePrimaryLocation(locationId);
    if (!success) {
      toast({
        title: "Error changing location",
        description: "Failed to switch location. Please try again.",
        variant: "error"
      });
    }
    progress.done();
    setSelectedRow(undefined);
    closeSearch("selection");
  };
  const handleThemeModeSelect = (themeMode: ThemeMode, resultIndex_3: number = 0) => {
    trackSelection("theme", resultIndex_3);
    setTheme(themeMode);
    setSearch("");
    setSelectedRow(undefined);
    closeSearch("selection");
  };
  const handleActionSelect = async (action: CommandAction, resultIndex_4: number = 0) => {
    trackSelection("action", resultIndex_4, action.id);
    setSearch("");
    setSelectedRow(undefined);
    closeSearch("selection");
    await action.onSelect();

    // Show toast for specific actions
    if (action.id === "copy-page-url") {
      toast({
        title: "URL Copied",
        description: "Page URL has been copied to clipboard.",
        variant: "success"
      });
    } else if (action.id === "copy-scheduling-link") {
      toast({
        title: "Link Copied",
        description: "Scheduling link has been copied to clipboard.",
        variant: "success"
      });
    } else if (action.id === "copy-tip-screen-link") {
      toast({
        title: "Link Copied",
        description: "Tip screen link has been copied to clipboard.",
        variant: "success"
      });
    }
  };
  const handleAskDecoda = (query: string) => {
    closeSearch("selection");
    useAskDecodaStore.getState().setInitialQuestion(query);
    useRightSidebarStore.getState().open(assistantSidebarDefinition, {
      targetId: askDecodaSidebarDefinition.id
    });
  };
  const handleCreatePatient = (resultIndex_5: number = 0) => {
    if (!parsedPatientName) return;
    trackSelection("create_patient", resultIndex_5);
    setSearch("");
    setSelectedRow(undefined);
    closeSearch("selection");
    useCommandPaletteModalsStore.getState().openModal(CommandPaletteModal.CREATE_PATIENT, {
      createPatient: {
        firstName: parsedPatientName.firstName,
        ...(parsedPatientName.lastName ? {
          lastName: parsedPatientName.lastName
        } : {})
      }
    });
  };
  if (!isOpen) return null;
  return <DialogPrimitives.Root open={isOpen} onOpenChange={setIsOpen}>
      <DialogPrimitives.Portal>
        <DialogPrimitives.Overlay className="fixed inset-0 z-[60] bg-background/15 backdrop-blur-[1px] force-transparent" onClick={() => closeSearch("backdrop_click")} />
        <DialogPrimitives.Content className="outline-none" onEscapeKeyDown={() => closeSearch("keyboard_shortcut")} asChild>
          <motion.div initial={{
          opacity: 0,
          scale: 0.8,
          x: "-50%"
        }} animate={{
          opacity: 1,
          scale: 1,
          x: "-50%"
        }} transition={{
          duration: 0.2
        }} className="fixed left-1/2 top-[20%] z-[70] w-[calc(100%-1.5rem)] max-w-3xl" ref={containerRef}>
            {/* The glow pulses on its own layer, and only its opacity: repainting
                the input's container every frame makes iOS keep re-placing the
                text cursor, so it drifts around while the bar is open. */}
            <motion.div aria-hidden className="pointer-events-none absolute inset-0 rounded-[20px]" style={{
            boxShadow: glowShadow
          }} animate={{
            opacity: [0.7, 1, 0.7]
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }} />
            <div className="relative overflow-hidden rounded-[20px] border border-border/30 bg-background/80 backdrop-blur-md">
              <div className="sr-only">
                <DialogPrimitives.Title>Command Menu</DialogPrimitives.Title>
                <DialogPrimitives.Description>
                  Search for pages, patients, and perform actions
                </DialogPrimitives.Description>
              </div>
              <div className="shadow-inner">
                <div className="flex items-center space-x-2 p-2 py-3">
                  {isLoading ? <LoadingSpinner size={14} className="ml-2" /> : <FaSearch size={14} className="ml-2 text-foreground-secondary" />}
                  <input ref={inputRef} value={search} onChange={e => setSearch(e.target.value)} className="h-full w-full border-none !bg-transparent p-1 text-lg text-foreground outline-none placeholder:text-foreground-muted focus:ring-0 md:text-body" placeholder="Search pages, patients, products, services..." autoFocus />
                </div>
              </div>
              <ScrollShadow.Root>
                <ScrollShadow.Viewport ref={scrollContainerRef}
              // Up to 400px, and no lower than the top of an on-screen keyboard
              // (--keyboard-inset): the bar sits 20% down, and 8rem covers its
              // input, footer and a margin.
              className="max-h-[min(400px,calc(80dvh_-_var(--keyboard-inset,0px)_-_8rem))] p-2 pb-2 *:focus-visible:ring-2 *:focus-visible:ring-primary">
                  <SearchResults search={search} items={{
                  ...currentItems,
                  patients: currentItems.patients as PatientSummary[],
                  catalogItems,
                  giftCards,
                  tenants: filteredTenants,
                  locations: filteredLocations,
                  themeModes: filteredThemeModes,
                  actions: filteredActions.actions,
                  settings: filteredActions.settings
                }} onSelect={(url_2, resultIndex_6, resultType_3) => {
                  // Click selections - result type provided by search-result-render
                  selectionMethodRef.current = "click";
                  handleSelect(url_2, resultType_3, resultIndex_6);
                }} onTenantSelect={(subDomain_0, resultIndex_7) => {
                  selectionMethodRef.current = "click";
                  handleTenantSelect(subDomain_0, resultIndex_7);
                }} onLocationSelect={(locationId_0, resultIndex_8) => {
                  selectionMethodRef.current = "click";
                  handleLocationSelect(locationId_0, resultIndex_8);
                }} onThemeModeSelect={(themeMode_0, resultIndex_9) => {
                  selectionMethodRef.current = "click";
                  handleThemeModeSelect(themeMode_0, resultIndex_9);
                }} onActionSelect={(action_0, resultIndex_10) => {
                  selectionMethodRef.current = "click";
                  handleActionSelect(action_0, resultIndex_10);
                }} onAskDecoda={showAskDecoda ? handleAskDecoda : undefined} createPatientCta={showCreatePatientCta && parsedPatientName ? {
                  firstName: parsedPatientName.firstName,
                  lastName: parsedPatientName.lastName,
                  onSelect: resultIndex_11 => {
                    selectionMethodRef.current = "click";
                    handleCreatePatient(resultIndex_11);
                  }
                } : undefined} selectedRow={selectedRow} totalRows={totalRows} scrollContainerRef={scrollContainerRef} isLoading={isLoading} />
                </ScrollShadow.Viewport>
                <ScrollShadow.Top />
                <ScrollShadow.Bottom />
              </ScrollShadow.Root>
              <div className="flex items-center justify-between border-t border-border bg-background-secondary/50 px-3 py-2 text-caption text-muted-foreground">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1">
                    <Kbd>↑</Kbd>
                    <Kbd>↓</Kbd>
                    <span className="ml-1">to navigate</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Kbd>Enter</Kbd>
                    <span className="ml-1">to select</span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Kbd>Esc</Kbd>
                  <span className="ml-1">to close</span>
                </div>
              </div>
            </div>
          </motion.div>
        </DialogPrimitives.Content>
      </DialogPrimitives.Portal>
    </DialogPrimitives.Root>;
};